import type { MapBounds } from "../types/map";

export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

export function calculateMapBounds(
  center: [number, number],
  zoomLevel: number,
  mapWidth: number = 800,
  mapHeight: number = 600,
): MapBounds {
  // Rough calculation for map bounds based on zoom level
  const latRange = (180 / Math.pow(2, zoomLevel)) * (mapHeight / 256);
  const lngRange = (360 / Math.pow(2, zoomLevel)) * (mapWidth / 256);

  return {
    north: center[0] + latRange / 2,
    south: center[0] - latRange / 2,
    east: center[1] + lngRange / 2,
    west: center[1] - lngRange / 2,
  };
}

export function isValidCoordinate(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

export function isTimestampWithinWindow(
  timestamp?: string | number | null,
  windowMs: number = 24 * 60 * 60 * 1000,
  nowMs: number = Date.now(),
): boolean {
  if (!timestamp) return false;
  const ts = new Date(timestamp).getTime();
  if (Number.isNaN(ts)) return false;
  return nowMs - ts < windowMs;
}

export function generateBikeId(): string {
  const prefix = "BIKE";
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${prefix}${timestamp}${random}`;
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: number | undefined;
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout !== undefined) clearTimeout(timeout);
    timeout = window.setTimeout(() => func.apply(this, args), wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function downloadAsJson(data: any, filename: string): void {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadAsCsv(csvString: string, filename: string): void {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  }

  // Fallback for older browsers
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  const success = document.execCommand("copy");
  document.body.removeChild(textArea);
  return Promise.resolve(success);
}
